/**
 * Connection cost and the "large media on Wi-Fi only" device preference.
 *
 * The Network Information API exists only in Chromium browsers. Where it is
 * missing the connection is `unknown`, and large media (video, or files over
 * LARGE_MEDIA_BYTES) is held for a person to send manually — data bundles are
 * expensive on site and a silent 80 MB video upload is not a kindness.
 */
import { useSyncExternalStore } from 'react';

import { readJson, writeJson } from '@/lib/storage';

export const LARGE_MEDIA_BYTES = 10 * 1024 * 1024;

export type ConnectionKind = 'unmetered' | 'metered' | 'unknown';

type NetworkInformationLike = { type?: string; saveData?: boolean };

export function connectionKind(
  connection: NetworkInformationLike | undefined = (
    globalThis.navigator as Navigator & { connection?: NetworkInformationLike }
  )?.connection,
): ConnectionKind {
  if (!connection) return 'unknown';
  if (connection.saveData) return 'metered';
  if (connection.type === 'cellular') return 'metered';
  if (connection.type === 'wifi' || connection.type === 'ethernet') return 'unmetered';
  return 'unknown';
}

export type MediaPreferences = {
  /** Hold video and large files until Wi-Fi (or a manual "Upload now"). */
  wifiOnlyLarge: boolean;
};

const KEY = 'kb.media_preferences';
const DEFAULTS: MediaPreferences = { wifiOnlyLarge: true };
let current: MediaPreferences = { ...DEFAULTS, ...(readJson<MediaPreferences>(KEY) ?? {}) };
const listeners = new Set<() => void>();

export function getMediaPreferences(): MediaPreferences {
  return current;
}

export function setMediaPreferences(patch: Partial<MediaPreferences>): void {
  current = { ...current, ...patch };
  writeJson(KEY, current);
  listeners.forEach((listener) => listener());
}

export function useMediaPreferences(): MediaPreferences {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getMediaPreferences,
    getMediaPreferences,
  );
}

export function isLargeMedia(item: { kind: string; size: number }): boolean {
  return item.kind === 'video' || item.size > LARGE_MEDIA_BYTES;
}

/** Whether an item waits for Wi-Fi instead of uploading automatically. */
export function heldForWifi(
  item: { kind: string; size: number; force: boolean },
  prefs: MediaPreferences = current,
  connection: ConnectionKind = connectionKind(),
): boolean {
  return prefs.wifiOnlyLarge && !item.force && isLargeMedia(item) && connection !== 'unmetered';
}
